// The workspace sidebar's head, in place of the shell's topbar: the
// sidebar's own search (lib/sidebarSearch.js), apart from what clicks narrow
// the board to (BoardScope), and the drawer's collapse at the end of the
// line. The search's clauses stand in its box as chips, the oldest folded
// behind "+N" where the box has no room for them all (FoldedChips). The box
// is one button: anywhere but a chip's close, or "+N" (which lists what it
// folds), it puts the finder over the page on the sidebar's search, as "/"
// does; from there the search can be handed on: to the board, or onto the
// workspace open (`handOff`). As tall as a rail button, beside the rail's
// first.
import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import FoldedChips from "./FoldedChips.jsx";
import HoverTooltip from "./HoverTooltip.jsx";
import SearchPalette, { Glass, clausesOf } from "./SearchPalette.jsx";
import { useBoardFinder } from "./BoardScope.jsx";
import { useShellControls } from "../lib/shellControls.js";
import { useSidebarSearch } from "../lib/sidebarSearch.js";

export function CollapseDrawer() {
  const shell = useShellControls();
  if (!shell?.onCollapse) return null;
  return (
    <HoverTooltip placement="bottom" label="Collapse the drawer">
      <button
        type="button"
        onClick={shell.onCollapse}
        aria-label="Collapse drawer"
        className="grid h-[24px] w-[24px] shrink-0 place-items-center text-[color:var(--muted)] hover:text-[color:var(--ink)] [&_svg]:h-[12px] [&_svg]:w-[12px]"
        data-sidebar-head-collapse=""
      >
        <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M10 3.5 5.5 8 10 12.5" />
        </svg>
      </button>
    </HoverTooltip>
  );
}

// A chip of the sidebar's box: its value alone (the box opened says each
// clause's field), to take out from here; a long value cut short as the
// board's chips cut theirs.
const CHIP = "inline-flex h-[24px] min-w-0 items-center gap-[4px] overflow-hidden rounded-[2px] border border-[color:var(--line)] bg-[color:var(--control-bg)] pl-[7px] pr-[1px] text-[12px]";
const CHIP_X = "grid h-[20px] w-[18px] shrink-0 place-items-center text-[14px] leading-none text-[color:var(--muted-soft)] hover:text-[color:var(--ink)]";
const MORE = `${CHIP} shrink-0 pr-[7px] text-[color:var(--ink)] hover:text-[color:var(--green)]`;

function drawChip(x, fit) {
  return (
    <span key={x.key} className={`${CHIP} ${fit}`} aria-label={`${x.negated ? "not " : ""}${x.field ? `${x.field} ` : ""}${x.value}`} data-sidebar-search-chip="">
      {x.negated ? <span className="shrink-0 text-[color:var(--muted)]">not</span> : null}
      <span className="min-w-0 max-w-[200px] truncate text-[color:var(--ink)]">{x.value}</span>
      <button type="button" aria-label={x.dropLabel} onClick={(e) => { e.stopPropagation(); x.drop(); }} className={CHIP_X}>&times;</button>
    </span>
  );
}

// The box holds the search's clauses as chips, each to take out; opened, the
// board's finder over the page on the sidebar's search, finding among what
// the board read.
function SidebarSearch({ onStart, openRef, handOff }) {
  const [own, setOwn] = useSidebarSearch();
  const lent = useBoardFinder();
  const shell = useShellControls();
  const [open, setOpen] = useState(false);
  const boxRef = useRef(null);
  const overRef = useRef(null);
  const openSheet = useCallback(() => setOpen(true), []);
  useEffect(() => {
    openRef.current = openSheet;
    return () => { openRef.current = null; };
  }, [openRef, openSheet]);
  useEffect(() => {
    if (open) overRef.current?.querySelector("[data-finder-input]")?.focus();
  }, [open]);
  const { pills, without } = clausesOf(own);
  const items = pills.map((p, i) => ({ key: `${p.text}:${i}`, field: p.field, negated: p.negated, value: p.value, drop: () => setOwn(without(i)), dropLabel: `Take ${p.value} out of the search` }));
  const vw = typeof window === "undefined" ? 1280 : window.innerWidth;
  const vh = typeof window === "undefined" ? 800 : window.innerHeight;
  const w = Math.min(1040, vw - 96);
  const extra = handOff ? { label: handOff.label, onClick: () => { handOff.run(); setOpen(false); } } : null;
  return (
    <>
      <div className="flex items-start gap-[4px]">
        <div
          ref={boxRef}
          role="button"
          tabIndex={0}
          aria-label="Search sessions"
          onClick={openSheet}
          // Only the box's own key: a chip's close or "+N" keeps its own.
          onKeyDown={(e) => { if (e.target === e.currentTarget && (e.key === "Enter" || e.key === " ")) { e.preventDefault(); openSheet(); } }}
          className="flex h-[38px] min-w-0 flex-1 cursor-pointer items-center gap-[4px] overflow-hidden rounded-[2px] border border-[color:var(--line-strong)] px-[6px] outline-none transition-colors hover:border-[color:color-mix(in_srgb,var(--muted)_55%,transparent)] hover:bg-[color:var(--gray-fill)] focus-visible:border-[color:color-mix(in_srgb,var(--muted)_55%,transparent)]"
          data-sidebar-search-open=""
        >
          <span className="mr-[4px] shrink-0 pl-[4px] text-[color:var(--muted)]"><Glass /></span>
          {items.length ? (
            <FoldedChips items={items} chip={drawChip} moreClassName={MORE} anchor={boxRef} />
          ) : (
            <span className="min-w-0 flex-1 truncate text-[13px] text-[color:var(--muted-soft)]">Search sessions</span>
          )}
          {items.length ? (
            <button type="button" aria-label="Clear the search" onClick={(e) => { e.stopPropagation(); setOwn(""); }} className="grid h-[36px] w-[24px] shrink-0 place-items-center text-[14px] leading-none text-[color:var(--muted)] hover:text-[color:var(--ink)]" data-sidebar-search-clear="">&times;</button>
          ) : (
            <span className="mr-[3px] shrink-0 rounded-[2px] border border-[color:var(--line)] px-[5px] text-[10px] leading-[16px] text-[color:var(--muted)]" aria-hidden="true">/</span>
          )}
        </div>
        <span className="flex h-[38px] items-center"><CollapseDrawer /></span>
      </div>
      {open ? createPortal(
        <div className="fixed inset-0 z-[900]" data-search-palette="">
          <div className="absolute inset-0" style={{ background: "color-mix(in srgb, var(--bg) 60%, transparent)" }} onMouseDown={() => setOpen(false)} aria-hidden="true" />
          <div
            ref={overRef}
            // The search's own line in the colour of what it finds under it.
            className="absolute left-1/2 -translate-x-1/2 bg-[color:var(--panel)]"
            style={{ top: "12vh", width: w }}
            onBlur={(e) => { if (!e.currentTarget.contains(e.relatedTarget)) setOpen(false); }}
            role="dialog"
            aria-label="Search sessions"
          >
            <SearchPalette
              query={own}
              setQuery={setOwn}
              kinds={lent.kinds}
              sessions={lent.sessions}
              reach={null}
              counts={false}
              clauseFor={lent.clauseFor}
              range={shell?.range}
              width={w}
              finderWidth={w}
              finderHeight={Math.max(240, Math.min(560, vh * 0.7))}
              onPreview={null}
              onStart={onStart}
              withPicks={false}
              placeholder="Search sessions: a person, an agent, a kind of work, an MCP server, a skill, a file..."
              extra={extra}
            />
          </div>
        </div>,
        document.body,
      ) : null}
    </>
  );
}

// `onStart`: starts a workspace from the search; `openRef`: set to what
// opens the search from elsewhere ("/"); `handOff`: what hands the sidebar's
// search on ({ label, run }), or null.
export default function WorkspaceSidebarHead({ onStart, openRef, handOff = null }) {
  return <SidebarSearch onStart={onStart} openRef={openRef} handOff={handOff} />;
}
